import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, Plus, Play, FlaskConical } from 'lucide-react';
import { get, post, del } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Textarea } from '@/components/atoms/Textarea';
import { Badge } from '@/components/atoms/Badge';
import { toast } from '@/store/toastStore';
import { confirmModal } from '@/store/confirmStore';

/**
 * 어드민 — 테스트 유저 + 자동 활동 (super 전용).
 *  - 테스트 유저 생성/삭제
 *  - 단건 게시글/댓글/추천
 *  - 전체 시나리오 자동 실행
 *  - 테스트 데이터 일괄 삭제
 * 모든 활동은 포인트 미지급, 생성 데이터는 is_test 로 표시되어 일괄 삭제 가능.
 */

interface TestUser {
  user_id: string;
  nickname: string;
  created_at: string;
  post_count: number;
  comment_count: number;
}
interface Board {
  id: number;
  slug: string;
  name: string;
}
interface RecentPost {
  id: number;
  title: string;
  is_test: boolean;
  board_name: string;
  author_nickname: string | null;
}

const card = 'rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5';
const label = 'mb-1 block text-sm font-medium text-[var(--color-text-sub)]';
const selectCls =
  'w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2 text-sm';

export function TestRunnerPage() {
  const qc = useQueryClient();

  const usersQ = useQuery({
    queryKey: ['admin', 'test', 'users'],
    queryFn: () => get<{ users: TestUser[] }>('/admin/test/users'),
  });
  const boardsQ = useQuery({
    queryKey: ['admin', 'test', 'boards'],
    queryFn: () => get<{ boards: Board[] }>('/admin/test/boards'),
  });
  const recentQ = useQuery({
    queryKey: ['admin', 'test', 'recent-posts'],
    queryFn: () => get<{ posts: RecentPost[] }>('/admin/test/recent-posts'),
  });

  const users = usersQ.data?.users ?? [];
  const boards = boardsQ.data?.boards ?? [];
  const recentPosts = recentQ.data?.posts ?? [];

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'test'] });
  };

  /* ── 테스트 유저 생성/삭제 ─────────────────────────────── */
  const [newNick, setNewNick] = useState('');
  const createUser = useMutation({
    mutationFn: (nickname: string) => post('/admin/test/users', { nickname }),
    onSuccess: () => {
      toast.success('테스트 유저를 생성했어요.');
      setNewNick('');
      qc.invalidateQueries({ queryKey: ['admin', 'test', 'users'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? '생성 실패'),
  });
  const removeUser = useMutation({
    mutationFn: (id: string) => del(`/admin/test/users/${id}`),
    onSuccess: () => {
      toast.success('삭제했어요.');
      qc.invalidateQueries({ queryKey: ['admin', 'test', 'users'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? '삭제 실패'),
  });

  /* ── 단건 게시글 ───────────────────────────────────────── */
  const [pUser, setPUser] = useState('');
  const [pBoard, setPBoard] = useState('');
  const [pTitle, setPTitle] = useState('');
  const [pContent, setPContent] = useState('');
  const createPost = useMutation({
    mutationFn: () =>
      post('/admin/test/post', {
        userId: pUser,
        boardId: Number(pBoard),
        title: pTitle || undefined,
        content: pContent || undefined,
      }),
    onSuccess: () => {
      toast.success('게시글을 작성했어요.');
      setPTitle('');
      setPContent('');
      refreshAll();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? '작성 실패'),
  });

  /* ── 단건 댓글 / 답글 ──────────────────────────────────── */
  const [cUser, setCUser] = useState('');
  const [cPost, setCPost] = useState('');
  const [cParent, setCParent] = useState(''); // 답글 대상 댓글 id ('' = 최상위)
  const [cContent, setCContent] = useState('');
  // 선택한 게시글의 댓글(최상위) — 답글 대상 선택용
  const postCommentsQ = useQuery({
    queryKey: ['admin', 'test', 'post-comments', cPost],
    queryFn: () =>
      get<{ comments: { id: number; content: string; author_nickname: string | null }[] }>(
        `/admin/test/post/${cPost}/comments`,
      ),
    enabled: !!cPost,
  });
  const postComments = postCommentsQ.data?.comments ?? [];
  const createComment = useMutation({
    mutationFn: () =>
      post('/admin/test/comment', {
        userId: cUser,
        postId: Number(cPost),
        content: cContent || undefined,
        parentId: cParent ? Number(cParent) : undefined,
      }),
    onSuccess: () => {
      toast.success(cParent ? '답글을 작성했어요.' : '댓글을 작성했어요.');
      setCContent('');
      qc.invalidateQueries({ queryKey: ['admin', 'test', 'post-comments', cPost] });
      refreshAll();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? '작성 실패'),
  });

  /* ── 단건 추천 ─────────────────────────────────────────── */
  const [vUser, setVUser] = useState('');
  const [vPost, setVPost] = useState('');
  const castVote = useMutation({
    mutationFn: () =>
      post<{ applied: boolean }>('/admin/test/vote', {
        userId: vUser,
        targetType: 'post',
        targetId: Number(vPost),
      }),
    onSuccess: (r: any) => {
      toast.success(r?.applied ? '추천했어요.' : '이미 추천한 글이에요.');
      refreshAll();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? '추천 실패'),
  });

  /* ── 전체 시나리오 ─────────────────────────────────────── */
  const [sUsers, setSUsers] = useState<string[]>([]);
  const [sBoards, setSBoards] = useState<number[]>([]);
  const [postsPerUser, setPostsPerUser] = useState(2);
  const [commentsPerPost, setCommentsPerPost] = useState(2);
  const [doVotes, setDoVotes] = useState(true);
  const scenario = useMutation({
    mutationFn: () =>
      post<{ posts: number; comments: number; votes: number }>('/admin/test/scenario', {
        userIds: sUsers,
        boardIds: sBoards,
        postsPerUser,
        commentsPerPost,
        doVotes,
      }),
    onSuccess: (r: any) => {
      toast.success(`완료 — 글 ${r.posts} · 댓글 ${r.comments} · 추천 ${r.votes}`);
      refreshAll();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? '실행 실패'),
  });

  /* ── 일괄 삭제 ─────────────────────────────────────────── */
  const purge = useMutation({
    mutationFn: () => del<{ posts: number; comments: number }>('/admin/test/data'),
    onSuccess: (r: any) => {
      toast.success(`테스트 데이터 삭제 — 글 ${r.posts} · 댓글 ${r.comments}`);
      refreshAll();
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? '삭제 실패'),
  });
  const handlePurge = async () => {
    const ok = await confirmModal({
      title: '테스트 데이터 일괄 삭제',
      message: '모든 테스트 게시글/댓글/추천을 영구 삭제합니다. 계속할까요?',
      confirmLabel: '삭제',
      tone: 'danger',
    });
    if (ok) purge.mutate();
  };

  const toggle = <T,>(arr: T[], v: T): T[] =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  return (
    <div className="w-full">
      <div className="mb-5 flex items-center gap-2">
        <FlaskConical size={20} className="text-[var(--color-primary)]" />
        <h1 className="text-lg font-bold">테스트 관리</h1>
        <Badge variant="editor">super</Badge>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* 테스트 유저 */}
        <section className={card}>
          <h2 className="mb-3 font-semibold">테스트 유저</h2>
          <div className="mb-3 flex gap-2">
            <Input
              placeholder="닉네임 (2~20자)"
              value={newNick}
              onChange={(e) => setNewNick(e.target.value)}
            />
            <Button
              variant="primary"
              onClick={() => newNick.trim() && createUser.mutate(newNick.trim())}
              loading={createUser.isPending}
            >
              <Plus size={16} className="mr-1" />추가
            </Button>
          </div>
          <ul className="divide-y divide-[var(--color-border)]">
            {users.length === 0 && (
              <li className="py-3 text-sm text-[var(--color-text-sub)]">테스트 유저가 없어요.</li>
            )}
            {users.map((u) => (
              <li key={u.user_id} className="flex items-center justify-between py-2.5">
                <div className="min-w-0">
                  <span className="font-medium">{u.nickname}</span>
                  <span className="ml-2 text-xs text-[var(--color-text-sub)]">
                    글 {u.post_count} · 댓글 {u.comment_count}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeUser.mutate(u.user_id)}
                  aria-label="삭제"
                >
                  <Trash2 size={15} />
                </Button>
              </li>
            ))}
          </ul>
        </section>

        {/* 단건 게시글 */}
        <section className={card}>
          <h2 className="mb-3 font-semibold">게시글 작성</h2>
          <div className="space-y-3">
            <div>
              <label className={label}>테스트 유저</label>
              <select className={selectCls} value={pUser} onChange={(e) => setPUser(e.target.value)}>
                <option value="">선택</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.nickname}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>게시판</label>
              <select className={selectCls} value={pBoard} onChange={(e) => setPBoard(e.target.value)}>
                <option value="">선택</option>
                {boards.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>
            <Input placeholder="제목 (비우면 자동 생성)" value={pTitle} onChange={(e) => setPTitle(e.target.value)} />
            <Textarea
              placeholder="내용 (비우면 자동 생성)"
              rows={3}
              value={pContent}
              onChange={(e) => setPContent(e.target.value)}
            />
            <Button
              variant="primary"
              fullWidth
              disabled={!pUser || !pBoard}
              loading={createPost.isPending}
              onClick={() => createPost.mutate()}
            >
              게시글 작성
            </Button>
          </div>
        </section>

        {/* 단건 댓글 */}
        <section className={card}>
          <h2 className="mb-3 font-semibold">댓글 작성</h2>
          <div className="space-y-3">
            <div>
              <label className={label}>테스트 유저</label>
              <select className={selectCls} value={cUser} onChange={(e) => setCUser(e.target.value)}>
                <option value="">선택</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.nickname}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>게시글 (최근, 공시 분석·증시뉴스 제외)</label>
              <select
                className={selectCls}
                value={cPost}
                onChange={(e) => {
                  setCPost(e.target.value);
                  setCParent(''); // 게시글 바뀌면 답글 대상 초기화
                }}
              >
                <option value="">선택</option>
                {recentPosts.map((p) => (
                  <option key={p.id} value={p.id}>
                    [{p.board_name}] {p.title.slice(0, 24)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>답글 대상 (선택 안 하면 일반 댓글)</label>
              <select
                className={selectCls}
                value={cParent}
                onChange={(e) => setCParent(e.target.value)}
                disabled={!cPost}
              >
                <option value="">일반 댓글로 작성</option>
                {postComments.map((c) => (
                  <option key={c.id} value={c.id}>
                    ↳ {c.author_nickname ?? '(알수없음)'}: {c.content.slice(0, 20)}
                  </option>
                ))}
              </select>
              {!!cPost && postComments.length === 0 && (
                <p className="mt-1 text-xs text-[var(--color-text-sub)]">이 글에는 아직 댓글이 없어요.</p>
              )}
            </div>
            <Textarea
              placeholder="내용 (비우면 자동 생성)"
              rows={2}
              value={cContent}
              onChange={(e) => setCContent(e.target.value)}
            />
            <Button
              variant="primary"
              fullWidth
              disabled={!cUser || !cPost}
              loading={createComment.isPending}
              onClick={() => createComment.mutate()}
            >
              {cParent ? '답글 작성' : '댓글 작성'}
            </Button>
          </div>
        </section>

        {/* 단건 추천 */}
        <section className={card}>
          <h2 className="mb-3 font-semibold">추천</h2>
          <div className="space-y-3">
            <div>
              <label className={label}>테스트 유저</label>
              <select className={selectCls} value={vUser} onChange={(e) => setVUser(e.target.value)}>
                <option value="">선택</option>
                {users.map((u) => (
                  <option key={u.user_id} value={u.user_id}>{u.nickname}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>게시글 (최근)</label>
              <select className={selectCls} value={vPost} onChange={(e) => setVPost(e.target.value)}>
                <option value="">선택</option>
                {recentPosts.map((p) => (
                  <option key={p.id} value={p.id}>
                    [{p.board_name}] {p.title.slice(0, 24)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              variant="primary"
              fullWidth
              disabled={!vUser || !vPost}
              loading={castVote.isPending}
              onClick={() => castVote.mutate()}
            >
              추천하기
            </Button>
          </div>
        </section>

        {/* 전체 시나리오 */}
        <section className={`${card} lg:col-span-2`}>
          <h2 className="mb-3 font-semibold">전체 시나리오 자동 실행</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={label}>테스트 유저 (다중 선택)</label>
              <div className="flex flex-wrap gap-2">
                {users.map((u) => (
                  <button
                    key={u.user_id}
                    type="button"
                    onClick={() => setSUsers((s) => toggle(s, u.user_id))}
                    className={`rounded-full border px-3 py-1 text-sm ${
                      sUsers.includes(u.user_id)
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                        : 'border-[var(--color-border)] text-[var(--color-text-sub)]'
                    }`}
                  >
                    {u.nickname}
                  </button>
                ))}
                {users.length === 0 && (
                  <span className="text-sm text-[var(--color-text-sub)]">먼저 테스트 유저를 만드세요.</span>
                )}
              </div>
            </div>
            <div>
              <label className={label}>게시판 (다중 선택)</label>
              <div className="flex flex-wrap gap-2">
                {boards.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setSBoards((s) => toggle(s, b.id))}
                    className={`rounded-full border px-3 py-1 text-sm ${
                      sBoards.includes(b.id)
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]'
                        : 'border-[var(--color-border)] text-[var(--color-text-sub)]'
                    }`}
                  >
                    {b.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div>
              <label className={label}>유저당 글 수</label>
              <Input
                type="number"
                min={1}
                max={20}
                value={postsPerUser}
                onChange={(e) => setPostsPerUser(Number(e.target.value))}
                className="w-24"
              />
            </div>
            <div>
              <label className={label}>글당 댓글 수</label>
              <Input
                type="number"
                min={0}
                max={20}
                value={commentsPerPost}
                onChange={(e) => setCommentsPerPost(Number(e.target.value))}
                className="w-24"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={doVotes} onChange={(e) => setDoVotes(e.target.checked)} />
              서로 추천
            </label>
            <Button
              variant="primary"
              disabled={sUsers.length === 0 || sBoards.length === 0}
              loading={scenario.isPending}
              onClick={() => scenario.mutate()}
            >
              <Play size={16} className="mr-1" />시나리오 실행
            </Button>
          </div>
        </section>

        {/* 일괄 삭제 */}
        <section className={`${card} lg:col-span-2`}>
          <h2 className="mb-1 font-semibold text-[var(--color-rise)]">테스트 데이터 일괄 삭제</h2>
          <p className="mb-3 text-sm text-[var(--color-text-sub)]">
            is_test 로 표시된 모든 게시글/댓글/추천을 영구 삭제합니다. (테스트 유저 계정은 유지)
          </p>
          <Button variant="danger" loading={purge.isPending} onClick={handlePurge}>
            <Trash2 size={16} className="mr-1" />테스트 데이터 모두 삭제
          </Button>
        </section>
      </div>
    </div>
  );
}
